import { Tab } from '@headlessui/react';
import { RectangleGroupIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import CartAccessory from 'components/shopping-cart/cart-accessory';
import DuctedSystemMap from 'components/systems/ducted-system-map';
import SystemMap from 'components/systems/system-map';
import { AccessoryProductType } from 'data/types';
import {
  updateSelectedDuctedSystem,
  updateSelectedSystem
} from 'features/systems';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { formatUSD } from 'utils/helpers';

export default function ShoppingCart() {
  const dispatch = useAppDispatch();
  const { selectedSystems, selectedDuctedSystems } = useAppSelector(
    (state) => state.systems
  );
  const [cartTab, setCartTab] = useState(0);
  const [subtotals, setSubtotals] = useState<number[]>([]);

  const updateSubtotals = () => {
    const newSubtotals: number[] = [];

    selectedDuctedSystems.forEach((system) => {
      let subtotal = 0;

      subtotal += system.price;

      system.quotableAccessories.forEach((accessory) => {
        subtotal += accessory.price * accessory.quantity;
      });

      newSubtotals.push(subtotal);
    });

    selectedSystems.forEach((system) => {
      let subtotal = 0;

      system.configuration.condensers.forEach((condenser) => {
        subtotal += condenser.selectedProduct?.price ?? 0;

        condenser.zones.forEach((zone) => {
          subtotal += zone.selectedProduct?.price ?? 0;
        });
      });

      system.quotableAccessories.forEach((accessory) => {
        subtotal += accessory.price * accessory.quantity;
      });

      newSubtotals.push(subtotal);
    });

    setSubtotals(newSubtotals);
  };

  useEffect(() => {
    updateSubtotals();
  }, [selectedSystems, selectedDuctedSystems]);

  const handleDuctlessAccessoryChange = (
    accessory: AccessoryProductType,
    quantity: number
  ) => {
    const newSystem = produce(selectedSystems[cartTab], (draft) => {
      const isSystemAccesory = draft.selectedAccessories.some(
        (acc) => acc.productId === accessory.productId
      );

      if (isSystemAccesory) {
        // eslint-disable-next-line no-param-reassign
        draft.selectedAccessories = draft.selectedAccessories.map((acc) => {
          if (acc.productId === accessory.productId) {
            return { ...acc, quantity };
          }
          return acc;
        });
      }

      const isCondenserAccesory = draft.configuration.condensers.some(
        (condenser) =>
          condenser.selectedAccessories.some(
            (acc) => acc.productId === accessory.productId
          )
      );

      if (isCondenserAccesory) {
        const condenserIndex = draft.configuration.condensers.findIndex(
          (condenser) =>
            condenser.selectedAccessories.some(
              (acc) => acc.productId === accessory.productId
            )
        );

        if (condenserIndex !== -1) {
          // eslint-disable-next-line no-param-reassign
          draft.configuration.condensers[condenserIndex].selectedAccessories =
            draft.configuration.condensers[
              condenserIndex
            ].selectedAccessories.map((acc) => {
              if (acc.productId === accessory.productId) {
                return { ...acc, quantity };
              }
              return acc;
            });
        }
      }
    });

    dispatch(updateSelectedSystem(newSystem));
  };

  const handleDuctedAccessoryChange = (
    accessory: AccessoryProductType,
    quantity: number
  ) => {
    const newSystem = produce(selectedDuctedSystems[cartTab], (draft) => {
      const isSystemAccesory = draft.selectedAccessories.some(
        (acc) => acc.productId === accessory.productId
      );

      if (isSystemAccesory) {
        // eslint-disable-next-line no-param-reassign
        draft.selectedAccessories = draft.selectedAccessories.map((acc) => {
          if (acc.productId === accessory.productId) {
            return { ...acc, quantity };
          }
          return acc;
        });
      }

      const isCondenserAccesory = draft.config.condensers.some((condenser) =>
        condenser.selectedAccessories.some(
          (acc) => acc.productId === accessory.productId
        )
      );

      if (isCondenserAccesory) {
        const condenserIndex = draft.config.condensers.findIndex((condenser) =>
          condenser.selectedAccessories.some(
            (acc) => acc.productId === accessory.productId
          )
        );

        if (condenserIndex !== -1) {
          // eslint-disable-next-line no-param-reassign
          draft.config.condensers[condenserIndex].selectedAccessories =
            draft.config.condensers[condenserIndex].selectedAccessories.map(
              (acc) => {
                if (acc.productId === accessory.productId) {
                  return { ...acc, quantity };
                }
                return acc;
              }
            );
        }
      }
    });

    dispatch(updateSelectedDuctedSystem(newSystem));
  };

  return (
    <div className='fixed right-6 top-[60px] flex max-h-[calc(100vh-240px)] w-[600px] flex-col divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow-xl'>
      <Tab.Group selectedIndex={cartTab} onChange={setCartTab}>
        <Tab.List
          className={`${
            selectedDuctedSystems.length + selectedSystems.length > 1
              ? 'flex'
              : 'hidden'
          } focus:outline-none`}
        >
          {[...selectedDuctedSystems, ...selectedSystems].map(
            (system, systemIndex) => (
              <Tab
                key={system.id}
                className='grow basis-0 py-3 text-center font-semibold focus:outline-none ui-selected:bg-blue-50 ui-selected:text-blue-600 ui-not-selected:bg-white ui-not-selected:text-gray-600'
              >
                Option {systemIndex + 1}
              </Tab>
            )
          )}
        </Tab.List>
        <div className='divide-y divide-gray-200 overflow-y-auto'>
          <Tab.Panels className='focus:outline-none'>
            {selectedDuctedSystems.map((system) => (
              <Tab.Panel key={system.id} className='focus:outline-none'>
                <div className='flex flex-col gap-5 p-5'>
                  {system.config.condensers.map((condenser) => (
                    <DuctedSystemMap
                      key={condenser.uniqueId}
                      condenser={condenser}
                      system={system}
                      showPrices
                    />
                  ))}
                  {system.quotableAccessories.length > 0 && (
                    <div>
                      <div className='flex items-center gap-2'>
                        <RectangleGroupIcon className='h-6 w-6 text-gray-800' />
                        <span className='text-xl font-semibold'>
                          Accessories
                        </span>
                      </div>
                      <div className='divide-y divide-gray-200'>
                        {system.quotableAccessories.map((accessory) => (
                          <CartAccessory
                            key={accessory.productId}
                            accessory={accessory}
                            onChange={(quantity) =>
                              handleDuctedAccessoryChange(accessory, quantity)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Tab.Panel>
            ))}
            {selectedSystems.map((system) => (
              <Tab.Panel key={system.id} className='focus:outline-none'>
                <div className='flex flex-col gap-5 p-5'>
                  {system.configuration.condensers.map((condenser) => (
                    <SystemMap
                      key={condenser.uniqueId}
                      condenser={condenser}
                      showPrices
                    />
                  ))}
                  {system.quotableAccessories.length > 0 && (
                    <div>
                      <div className='flex items-center gap-2'>
                        <RectangleGroupIcon className='h-6 w-6 text-gray-800' />
                        <span className='text-xl font-semibold'>
                          Accessories
                        </span>
                      </div>
                      <div className='divide-y divide-gray-200'>
                        {system.quotableAccessories.map((accessory) => (
                          <CartAccessory
                            key={accessory.productId}
                            accessory={accessory}
                            onChange={(quantity) =>
                              handleDuctlessAccessoryChange(accessory, quantity)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </div>
        <div className='flex items-center justify-end gap-3 p-5'>
          <span className='font-semibold text-gray-600'>Subtotal:</span>
          <span className='text-2xl font-bold'>
            {formatUSD(subtotals[cartTab] || 0, true)}
          </span>
        </div>
      </Tab.Group>
    </div>
  );
}
